import { useQueries, useQuery } from "@tanstack/react-query";
import { publicServerEndpoint } from "../../services/helper";
import { ALL_PRODUCTS_OBJECT } from "./CollectionHome";

export const useCollectionsQuery = (storeId) => {
  return useQuery({
    queryKey: ["collections", storeId],
    queryFn: async () => {
      const url = `${publicServerEndpoint}/v3/collection/store/${storeId}?limit=5`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch collections: ${response.status}`);
      }
      const collections = await response.json();
      if (collections && collections.collections && collections.collections.length === 0) {
        collections.collections.push(ALL_PRODUCTS_OBJECT);
      }

      return collections.collections;
    },
    enabled: !!storeId,
  });
};

export const useCollectionWithStoreProducts = (storeId) => {
  const { data: collections } = useCollectionsQuery(storeId);
  const safeCollections = collections || [];

  const productQueries = useQueries({
    queries: safeCollections?.map((collection) => ({
      queryKey: ["collectionProducts", storeId, collection._id],
      queryFn: async () => {
        const idParam = collection._id === "all" ? "" : collection._id;
        const url = `${publicServerEndpoint}/v3/product/store/${storeId}/collectionv2?page=1&limit=10&source=web&collectionId=${idParam}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.status}`);
        }
        const data = await response.json();
        return data.data?.docs || [];
      },
      enabled: !!storeId,
    })),
  });

  const combinedCollections = safeCollections.map((collection, index) => ({
    collection,
    products: productQueries[index]?.data || [],
    isLoading: productQueries[index]?.isLoading,
  }));

  return {
    data: combinedCollections,
    loading: productQueries.some((query) => query.isLoading),
  };
};

/**
 * Fetches products for multiple collections
 * @param {string} storeId - Store ID
 * @param {Array} collections - Array of collection objects
 * @returns {Promise<Array>} - Collections with their products
 */
export const fetchProductsForCollections = async (storeId, collections, productLimit = 10) => {
  if (!collections || collections.length === 0) return [];

  try {
    const collectionsWithProducts = await Promise.all(
      collections.map(async (collection) => {
        try {
          const idParam = collection._id === "all" ? "" : collection._id;
          const url = `${publicServerEndpoint}/v3/product/store/${storeId}/collectionv2?page=1&limit=${productLimit}&source=web&collectionId=${idParam}`;
          const response = await fetch(url);

          if (!response.ok) {
            console.warn(
              `Failed to fetch products for collection ${collection._id}: ${response.status}`,
            );
            return {
              ...collection,
              products: [],
            };
          }

          const data = await response.json();

          return {
            ...collection,
            products: data.data?.docs || [],
          };
        } catch (error) {
          console.error(`Error fetching products for collection ${collection._id}:`, error);
          return {
            ...collection,
            products: [],
          };
        }
      }),
    );

    return collectionsWithProducts;
  } catch (error) {
    console.error("Error in fetchProductsForCollections:", error);
    return collections.map((collection) => ({ ...collection, products: [] }));
  }
};
